import { useEffect, useRef, useState } from "react";
import { playerStore, usePlayer } from "@/lib/playerStore";
import {
  getOfflineAudioUrl,
} from "@/lib/offlineEpisodes";
import {
  fetchRemoteResume,
  getLocalResume,
  saveResume,
  clearResume,
} from "@/lib/resumePlayback";
import { upNextStore } from "@/lib/upNextStore";

/**
 * Owns the single <audio> element for the whole app. Mounted once in App.tsx
 * so playback persists across route changes — switching pages no longer
 * destroys the player. Also wires up the MediaSession API so iOS / Android
 * lock-screen and Dynamic Island controls work, matching the Spotify /
 * Apple Podcasts experience.
 */
export default function GlobalAudioHost() {
  const audioRef = useRef<HTMLAudioElement>(null);
  const current = usePlayer((s) => s.current);
  const playing = usePlayer((s) => s.playing);
  const muted = usePlayer((s) => s.muted);
  const seekTarget = usePlayer((s) => s.seekTarget);
  const seekSignal = usePlayer((s) => s.seekSignal);

  const [effectiveSrc, setEffectiveSrc] = useState<string>("");
  const blobUrlRef = useRef<string | null>(null);
  const lastSaveRef = useRef(0);
  const resumeAppliedRef = useRef(false);

  // ---- Resolve offline blob URL when an episode is loaded ----
  useEffect(() => {
    let cancelled = false;
    resumeAppliedRef.current = false;
    (async () => {
      if (!current?.src) {
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
          blobUrlRef.current = null;
        }
        setEffectiveSrc("");
        return;
      }
      let nextSrc = current.src;
      if (current.episodeId) {
        try {
          const localUrl = await getOfflineAudioUrl(current.episodeId);
          if (localUrl) nextSrc = localUrl;
        } catch { /* ignore — fall back to network */ }
      }
      if (cancelled) {
        if (nextSrc !== current.src && nextSrc.startsWith("blob:")) {
          URL.revokeObjectURL(nextSrc);
        }
        return;
      }
      if (blobUrlRef.current && blobUrlRef.current !== nextSrc) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      if (nextSrc.startsWith("blob:")) blobUrlRef.current = nextSrc;
      setEffectiveSrc(nextSrc);
    })();
    return () => {
      cancelled = true;
    };
  }, [current?.src, current?.episodeId]);

  useEffect(() => () => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, []);

  // ---- Drive play / pause from store state (always responding to a user gesture) ----
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.play().catch(() => {});
    } else {
      a.pause();
    }
  }, [playing, effectiveSrc]);

  useEffect(() => {
    const a = audioRef.current;
    if (a) a.muted = muted;
  }, [muted]);

  // ---- Apply seek requests ----
  useEffect(() => {
    if (seekSignal === 0) return;
    const a = audioRef.current;
    if (!a || seekTarget == null) return;
    const doSeek = () => {
      try { a.currentTime = seekTarget; } catch { /* ignore */ }
      a.play().catch(() => {});
      playerStore._consumeSeek();
    };
    if (a.readyState >= 1) doSeek();
    else a.addEventListener("loadedmetadata", doSeek, { once: true });
  }, [seekSignal, seekTarget]);

  // ---- Hydrate resume position when a new track loads ----
  useEffect(() => {
    if (!current) return;
    let cancelled = false;
    const localT = getLocalResume(current.src);
    (async () => {
      const remoteT = await fetchRemoteResume(current.episodeId);
      if (cancelled) return;
      const resumeT = remoteT != null && (localT == null || remoteT > localT) ? remoteT : localT;
      if (resumeT == null) return;
      const a = audioRef.current;
      if (!a || resumeAppliedRef.current) return;
      const apply = () => {
        if (resumeAppliedRef.current) return;
        // Don't override an explicit seek-to-moment from search.
        if (current.startTime && current.startTime > resumeT + 30) return;
        try { a.currentTime = resumeT; } catch { /* ignore */ }
        resumeAppliedRef.current = true;
      };
      if (a.readyState >= 1) apply();
      else a.addEventListener("loadedmetadata", apply, { once: true });
    })();
    return () => { cancelled = true; };
  }, [current?.key]);

  // Refresh Up Next whenever a new track loads so the panel reflects the
  // current playlist/context.
  useEffect(() => {
    if (!current?.episodeId) return;
    void upNextStore.refresh();
  }, [current?.episodeId]);

  // ---- Wire <audio> events back into the store + MediaSession + resume saves ----
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;

    const onLoaded = () => {
      playerStore._reportTime(a.currentTime, a.duration || 0);
      playerStore._reportBuffering(false);
      updatePositionState();
    };
    const onTime = () => {
      playerStore._reportTime(a.currentTime, a.duration || 0);
      const now = Date.now();
      if (now - lastSaveRef.current > 5000) {
        lastSaveRef.current = now;
        const c = playerStore.getState().current;
        saveResume(c?.src, c?.episodeId, a.currentTime, a.duration || 0);
        // Mark complete at 90% so the episode stops appearing in Up Next.
        const d = a.duration || 0;
        if (d > 0 && a.currentTime / d >= 0.9 && c?.episodeId) {
          void upNextStore.markCompleted(c.episodeId);
        }
      }
    };
    const onPlay = () => {
      playerStore._reportPlaying(true);
      playerStore._reportBuffering(false);
    };
    const onPause = () => {
      playerStore._reportPlaying(false);
      const c = playerStore.getState().current;
      saveResume(c?.src, c?.episodeId, a.currentTime, a.duration || 0);
    };
    const onWaiting = () => playerStore._reportBuffering(true);
    const onStalled = () => playerStore._reportBuffering(true);
    const onLoadStart = () => playerStore._reportBuffering(true);
    const onPlaying = () => playerStore._reportBuffering(false);
    const onCanPlay = () => playerStore._reportBuffering(false);
    const onEmptied = () => {
      // src changed / element reset — clear stale progress so the bar doesn't
      // briefly show the previous track's position while the new one loads.
      playerStore._reportTime(0, 0);
      playerStore._reportBuffering(true);
    };
    const onEnded = () => {
      playerStore._reportPlaying(false);
      playerStore._reportBuffering(false);
      const c = playerStore.getState().current;
      clearResume(c?.src, c?.episodeId);
      if (c?.episodeId) void upNextStore.markCompleted(c.episodeId);
      updatePositionState();
      // Auto-advance to the next item in the queue.
      const advanced = upNextStore.consumeHead();
      if (!advanced) void upNextStore.refresh();
    };

    a.addEventListener("loadedmetadata", onLoaded);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    a.addEventListener("ended", onEnded);
    a.addEventListener("waiting", onWaiting);
    a.addEventListener("stalled", onStalled);
    a.addEventListener("loadstart", onLoadStart);
    a.addEventListener("playing", onPlaying);
    a.addEventListener("canplay", onCanPlay);
    a.addEventListener("emptied", onEmptied);
    return () => {
      a.removeEventListener("loadedmetadata", onLoaded);
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("ended", onEnded);
      a.removeEventListener("waiting", onWaiting);
      a.removeEventListener("stalled", onStalled);
      a.removeEventListener("loadstart", onLoadStart);
      a.removeEventListener("playing", onPlaying);
      a.removeEventListener("canplay", onCanPlay);
      a.removeEventListener("emptied", onEmptied);
    };
    // Depend on effectiveSrc so listeners re-bind once the <audio> element
    // actually mounts (it's null on first render while we resolve the src).
  }, [effectiveSrc]);

  // ---- MediaSession metadata + handlers (drives lock-screen / Dynamic Island UI) ----
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    const a = audioRef.current;
    if (!a) return;

    if (current) {
      try {
        navigator.mediaSession.metadata = new MediaMetadata({
          title: current.title || "Episode",
          artist: current.podcastName || "",
          album: current.podcastName || "",
          artwork: current.artworkUrl
            ? [
                { src: current.artworkUrl, sizes: "96x96", type: "image/jpeg" },
                { src: current.artworkUrl, sizes: "192x192", type: "image/jpeg" },
                { src: current.artworkUrl, sizes: "256x256", type: "image/jpeg" },
                { src: current.artworkUrl, sizes: "512x512", type: "image/jpeg" },
              ]
            : [],
        });
      } catch { /* ignore */ }
    } else {
      try { navigator.mediaSession.metadata = null; } catch { /* ignore */ }
    }

    const setHandler = (action: MediaSessionAction, handler: MediaSessionActionHandler | null) => {
      try { navigator.mediaSession.setActionHandler(action, handler); } catch { /* unsupported */ }
    };

    setHandler("play", () => { a.play().catch(() => {}); playerStore._reportPlaying(true); });
    setHandler("pause", () => { a.pause(); playerStore._reportPlaying(false); });
    setHandler("seekbackward", (details) => {
      const skip = details.seekOffset || 15;
      a.currentTime = Math.max(0, a.currentTime - skip);
      updatePositionState();
    });
    setHandler("seekforward", (details) => {
      const skip = details.seekOffset || 30;
      a.currentTime = Math.min(a.duration || 0, a.currentTime + skip);
      updatePositionState();
    });
    setHandler("seekto", (details) => {
      if (details.seekTime != null) {
        a.currentTime = details.seekTime;
        updatePositionState();
      }
    });
    setHandler("nexttrack", () => {
      const advanced = upNextStore.consumeHead();
      if (!advanced) void upNextStore.refresh();
    });
    setHandler("previoustrack", () => {
      a.currentTime = Math.max(0, a.currentTime - 15);
      updatePositionState();
    });

    return () => {
      // Don't strip handlers on unmount — host stays mounted for app lifetime.
    };
  }, [current?.key]);

  function updatePositionState() {
    if (!("mediaSession" in navigator)) return;
    const a = audioRef.current;
    if (!a || !isFinite(a.duration) || !a.duration) return;
    try {
      navigator.mediaSession.setPositionState({
        duration: a.duration,
        playbackRate: a.playbackRate || 1,
        position: a.currentTime,
      });
    } catch { /* ignore */ }
  }

  // Keep lock-screen scrubber in sync periodically.
  useEffect(() => {
    if (!current) return;
    const id = window.setInterval(updatePositionState, 1000);
    return () => window.clearInterval(id);
  }, [current?.key]);

  if (!effectiveSrc) return null;

  // playsInline + preload="metadata" keep iOS happy. NEVER set autoPlay on the
  // element — playback is started in response to user gestures via the store.
  return (
    <audio
      ref={audioRef}
      src={effectiveSrc}
      preload="metadata"
      playsInline
      {...({ "webkit-playsinline": "true" } as Record<string, string>)}
      muted={muted}
      style={{ display: "none" }}
    />
  );
}
